'use client'
import { useComponentTranslation } from '@core/i18n'
import { Box, Divider, Flex, Tag, Text, Tooltip } from '@core/ui-elements'
import {
  _IAssortmentType,
  _IDeliveryStatus,
  _IDeliveryType,
  IDeliveryNormalized,
} from '@data/types'
import { deliveryStatusClassification } from '@utils/arrays'
import { getStatusTime, getTimerLimits } from '@utils/datetime'
import { firstAndLastNames, lastNamesAbbreviator } from '@utils/formatters'
import { CardColors } from '@utils/styles'
import copy from 'copy-to-clipboard'
import { FC, MouseEvent, useEffect, useState } from 'react'

import { CardBorderStyles, cardColorSchema } from '../card'
import { CoreScheduledTag } from '../core-scheduled-tag'
import { CountdownTimer } from '../countdown-timer'
import { EtaTimer } from '../eta-timer'
import { StatCard } from '../stats-cards'
import { SuperDakiTag } from '../super-daki-tag'

export interface IDeliveryCardContent {
  delivery: IDeliveryNormalized
  secondsCountdown?: number
  columnType: deliveryStatusClassification
  showRider?: boolean
  showShopper?: boolean
  warningThresholdPercent?: number
  criticalThresholdPercent?: number
  setStructureColor: (color: CardColors) => void
  showOfferingInRider?: boolean
  hideCustomerOrderCount: boolean
}

export const DeliveryCardContent: FC<IDeliveryCardContent> = ({
  delivery,
  secondsCountdown,
  columnType,
  showRider = true,
  showShopper = true,
  warningThresholdPercent,
  criticalThresholdPercent,
  setStructureColor,
  showOfferingInRider = true,
  hideCustomerOrderCount,
}) => {
  const t = useComponentTranslation('DeliveryCardContent')
  const [tooltipMsg, setTooltipMsg] = useState('Copiar código')
  const [cardColor, setCardColor] = useState<CardColors>(CardColors.grayWhite)

  const isSuperDaki = delivery.assortmentType === _IAssortmentType.plus
  const isCoreScheduled =
    delivery.assortmentType === _IAssortmentType.core &&
    delivery.deliveryType === _IDeliveryType.scheduled
  const orderIdSlices = delivery.orderId.split('-')
  const orderId = orderIdSlices[orderIdSlices.length - 1]
  const { warningThresholdMS, criticalThresholdMS, handleColorChange } =
    getTimerLimits({
      secondsCountdown,
      warningThresholdPercent,
      criticalThresholdPercent,
      defaultColor: CardColors.grayWhite,
    })

  const handleTimeChange = (time: number) => {
    if (!warningThresholdMS || !criticalThresholdMS) return

    const color = handleColorChange(time)

    if (cardColor !== color) {
      setCardColor(color)
    }
  }

  const handlerCopyOrderId = (
    event: MouseEvent<HTMLParagraphElement, globalThis.MouseEvent>
  ) => {
    event.stopPropagation()
    copy(orderId)
    setTooltipMsg(t('Code copied!'))
  }

  const isBgColorWhite =
    cardColor === CardColors.grayWhite || cardColor === CardColors.blueWhite

  const dividerColor =
    cardColorSchema[isSuperDaki ? CardColors.blueWhite : cardColor].body[
      CardBorderStyles.left
    ].borderLeftColor

  const currentStatus = {
    [deliveryStatusClassification.processing]: _IDeliveryStatus.picking,
    [deliveryStatusClassification.outForDelivery]: _IDeliveryStatus.on_the_way,
    [deliveryStatusClassification.ready]: _IDeliveryStatus.ready,
    [deliveryStatusClassification.pending]:
      isSuperDaki || isCoreScheduled
        ? _IDeliveryStatus.processed
        : _IDeliveryStatus.pre_processed,
    [deliveryStatusClassification.unknown]: delivery.status,
  }

  const processedTime = getStatusTime(
    delivery.statusHistory,
    _IDeliveryStatus.processed
  )
  const currentStatusTime = getStatusTime(
    delivery.statusHistory,
    currentStatus[columnType]
  )

  const countdownTooltipLabel =
    columnType === deliveryStatusClassification.pending
      ? t('Ideal time to start preparing')
      : t('Ideal time to finish preparation')

  useEffect(() => {
    setStructureColor(cardColor)
  }, [cardColor, setStructureColor])

  return (
    <>
      <SuperDakiTag
        etaMax={delivery.etaMax}
        etaMin={delivery.etaMin}
        showTag={isSuperDaki}
      />
      <CoreScheduledTag
        bgColor={isBgColorWhite ? undefined : 'white'}
        etaMax={delivery.etaMax}
        etaMin={delivery.etaMin}
        showTag={isCoreScheduled}
      />
      <StatCard
        label={
          <Tooltip
            hasArrow
            aria-label="Copiar código"
            closeOnClick={false}
            label={tooltipMsg}
            placement="top"
            onClose={() => setTooltipMsg(t('Copy code'))}
          >
            <Text cursor={'pointer'} onClick={handlerCopyOrderId}>
              {orderId.slice(-5)}
            </Text>
          </Tooltip>
        }
        rightSize={
          <Flex
            flexDir={'row'}
            flexWrap={'wrap'}
            gap={'0.5rem'}
            justifyContent={'flex-end'}
          >
            {secondsCountdown ? (
              <Tooltip
                hasArrow
                aria-label="countdown-tooltip"
                label={countdownTooltipLabel}
                placement="top"
                shouldWrapChildren={true}
              >
                <CountdownTimer
                  bgColor={isBgColorWhite ? undefined : 'white'}
                  callAtTime={handleTimeChange}
                  colorIcon={cardColor}
                  countTime={secondsCountdown + 1}
                  targetTime={currentStatusTime}
                />
              </Tooltip>
            ) : null}{' '}
            <EtaTimer
              ETAMax={
                isSuperDaki || isCoreScheduled ? undefined : delivery.etaMax
              }
              bgColor={isBgColorWhite ? undefined : 'white'}
              initialTime={
                isSuperDaki || isCoreScheduled
                  ? processedTime
                  : delivery.confirmedAt
              }
            />
          </Flex>
        }
      />
      <StatCard
        content={
          <Box>
            <Text
              overflow={'hidden'}
              textOverflow={'ellipsis'}
              textTransform={'capitalize'}
              translate="no"
              whiteSpace={'nowrap'}
              width={'100%'}
            >
              {delivery.customer.address.formatted}
            </Text>
            <Text
              color={'gray.600'}
              textTransform={'capitalize'}
              translate="no"
            >
              {delivery.customer.address.neighborhood}
            </Text>
          </Box>
        }
        label={
          <Text textTransform={'capitalize'} translate="no">
            {lastNamesAbbreviator(delivery.customer.name)}
          </Text>
        }
        rightSize={
          !hideCustomerOrderCount && delivery.customer.orderCount ? (
            <Tag backgroundColor={isBgColorWhite ? undefined : 'white'}>
              {t('Order', { count: delivery.customer.orderCount })}
            </Tag>
          ) : null
        }
      />
      {showRider || showShopper ? (
        <>
          <Divider borderColor={dividerColor} borderWidth={'0.03rem'} />
          <Flex
            flexDirection={'row'}
            flexWrap={'nowrap'}
            gap={'0.5rem'}
            width={'100%'}
          >
            {showShopper ? (
              <StatCard
                content={
                  <Text textTransform={'capitalize'} translate="no">
                    {t('Shopper')}
                  </Text>
                }
                direction={'column-reverse'}
                label={
                  <Text
                    overflow={'hidden'}
                    textOverflow={'ellipsis'}
                    textTransform={'capitalize'}
                    translate="no"
                    whiteSpace={'nowrap'}
                    width={'100%'}
                  >
                    {lastNamesAbbreviator(
                      firstAndLastNames(delivery.shopper?.fullName)
                    ) || '-'}
                  </Text>
                }
                style={{
                  width: '50%',
                }}
              />
            ) : null}
            {showRider ? (
              <StatCard
                content={<Text translate="no">{t('Rider')}</Text>}
                direction={'column-reverse'}
                label={
                  <Text
                    overflow={'hidden'}
                    textOverflow={'ellipsis'}
                    translate="no"
                    whiteSpace={'nowrap'}
                    width={'100%'}
                  >
                    {delivery.rider?.fullName ? (
                      firstAndLastNames(delivery.rider?.fullName)
                    ) : showOfferingInRider ? (
                      <Text as="span" color="yellow.600">
                        {t('Offering')}
                      </Text>
                    ) : (
                      '-'
                    )}
                  </Text>
                }
                style={{
                  width: '50%',
                }}
              />
            ) : null}
          </Flex>
        </>
      ) : null}
    </>
  )
}
